/**
 * La tournée, vue par l'écran de préparation — tout le calcul, hors de React.
 *
 * Même découpe que `planning.ts`, `ot.ts` et `chargement.ts`, et pour la même
 * raison : ce qui est ici se vérifie sans navigateur, sans base et sans
 * session. C'est le fichier où sont les fautes possibles, donc c'est celui qui
 * est écrit et éprouvé AVANT l'écran.
 *
 *
 * CINQ CHOSES QUE CE FICHIER SAIT
 * ---------------------------------------------------------------------------
 *
 *   - **la période d'une tournée se CALCULE**, du premier au dernier arrêt.
 *     L'utilisateur avait d'abord demandé un début et une fin saisis, comme la
 *     v1, puis a validé le calcul en voyant l'essai. Deux dates saisies à la
 *     main finissent par ne plus correspondre aux arrêts, et personne ne voit
 *     l'écart ;
 *
 *   - **c'est le MAXIMUM atteint qui compte**, jamais le total chargé dans la
 *     journée. Un camion qui charge 20 palettes, les livre, puis en reprend 20
 *     n'a jamais eu que 20 palettes à bord ;
 *
 *   - **le relais fausse le cumul dans les deux sens**, et c'est le point
 *     délicat de tout l'écran. Voir `cumuler` ;
 *
 *   - **le prix d'un ordre est compté sur la tournée qui fait son
 *     CHARGEMENT.** Sans cette règle, un ordre en relais serait compté deux
 *     fois et la somme des tournées dépasserait le chiffre d'affaires réel ;
 *
 *   - **on range au fond ce qui se livre en dernier**, et on mélange les
 *     supports dans un même rang quand la largeur restante le permet.
 *
 *
 * CE QUE CE FICHIER NE FAIT PAS
 * ---------------------------------------------------------------------------
 * Il ne dessine pas. La projection isométrique est dans `isometrie.ts`, qui
 * consomme le rangement calculé ici. Un fichier qui range et un fichier qui
 * projette : la première erreur du 04/09 est venue d'avoir mélangé une
 * convention de comptage et une géométrie de dessin.
 *
 * Il ne calcule pas non plus le mètre linéaire d'un support — c'est
 * `packages/core/src/chargement.ts`. **Le ML unitaire arrive ici en ENTRÉE**,
 * sur l'arrêt.
 *
 *
 * D'OÙ VIENT LE ML UNITAIRE — TRANCHÉ LE 05/09/2026
 * ---------------------------------------------------------------------------
 * De l'ÉTAPE, où il est **figé à la saisie** par l'API. Mot pour mot, de
 * l'utilisateur : « lorsque l'utilisateur met 10 palettes 80×120, l'app doit
 * calculer automatiquement le ML correspondant et le mètre à coter ». Le ML ne
 * se saisit donc jamais, et il ne se recalcule jamais à l'affichage : corriger
 * les dimensions d'un support dans les Paramètres ne doit pas réécrire ce qu'on
 * a facturé le mois dernier. Même règle que l'adresse du site et le libellé du
 * support.
 *
 * **10 palettes 80×120 valent 4,00 ML et non 4,80** — la convention, pas la
 * géométrie. Les 10 palettes occupent bien quatre rangs de plancher, dont le
 * dernier n'a qu'une palette, mais le métier facture 10 × 0,40. Confirmé par
 * l'utilisateur le 05/09 : « c'est 4ml ». C'est `mlOccupes` qui le calcule.
 *
 * `ml_unitaire` reste `null` quand le support n'a pas de dimensions. **Un ML
 * inconnu ne vaut pas zéro** : l'écran se tait.
 */

import { mlLisible } from '@stafa/core';

export { mlLisible };

// ---------------------------------------------------------------------------
// Ce que l'API rend
// ---------------------------------------------------------------------------

/**
 * Un arrêt d'une tournée, tel que `SELECTION_ARRETS` le rend
 * (`apps/api/src/tournees.ts`), allégé de ce qui ne sert pas au calcul.
 *
 * Les nombres arrivent en CHAÎNES : le pilote postgres rend `numeric` en texte,
 * pour ne pas perdre de décimales sur les grands nombres. `EtapePlanning` a la
 * même particularité. Rien ici ne suppose le contraire — voir `nombre`.
 */
export interface Arret {
  readonly id: string;
  readonly tournee_id: string | null;
  readonly rang_tournee: number | null;
  readonly rang_chargement: number | null;
  readonly ot_id: string;
  /** Le numéro de l'ordre, `26000001`. */
  readonly numero: string;
  readonly client: string | null;
  readonly prix_vente: string | number | null;
  /** `'chargement'` ou `'livraison'` — la base n'accepte rien d'autre. */
  readonly type: string;
  readonly rang: number;
  readonly site_nom: string;
  readonly site_ville: string | null;
  readonly jour: string;
  readonly heure_debut: string | null;
  readonly quantite: string | number | null;
  /** Le texte figé du support, `Palette 80×120`. */
  readonly support: string | null;
  /**
   * Les mètres linéaires d'UNE unité, figés sur l'étape à la saisie. `null`
   * quand le support n'a pas de dimensions — voir l'en-tête.
   *
   * `string | number` comme les autres `numeric` de cette interface : le
   * pilote postgres les rend en TEXTE. Le typer `number` seul rendrait
   * `Number.isFinite('0.400')` faux, et tous les remplissages passeraient pour
   * inconnus sans que rien ne le signale.
   */
  readonly ml_unitaire: string | number | null;
}

/** Une tournée, telle que `SELECTION_TOURNEES` la rend. */
export interface Tournee {
  readonly id: string;
  readonly numero: string;
  readonly capacite_ml: string | number | null;
  readonly remorque_id: string | null;
  readonly remorque_immatriculation: string | null;
  readonly vehicule_immatriculation: string | null;
  readonly chauffeur_nom: string | null;
}

/**
 * Un nombre venu de l'API, quelle que soit sa forme.
 *
 * `null` pour tout ce qui n'est pas un nombre fini : une chaîne vide, un
 * `NaN`, un `Infinity`. **Jamais zéro par défaut** — c'est la règle de tout le
 * calcul du chargement : un zéro se lit « rien », alors qu'on ne sait pas.
 */
export function nombre(valeur: string | number | null | undefined): number | null {
  if (valeur === null || valeur === undefined) return null;
  if (typeof valeur === 'number') return Number.isFinite(valeur) ? valeur : null;
  const propre = valeur.trim();
  if (propre === '') return null;
  const lu = Number(propre);
  return Number.isFinite(lu) ? lu : null;
}

// ---------------------------------------------------------------------------
// La période
// ---------------------------------------------------------------------------

/** Du premier au dernier arrêt. `null` sur une tournée vide. */
export interface Periode {
  readonly debut: string;
  readonly fin: string;
  /** Vrai quand la tournée découche : « 04/09 → 05/09 ». */
  readonly plusieursJours: boolean;
}

/**
 * La période d'une tournée, calculée de ses arrêts.
 *
 * Comparaison de CHAÎNES `AAAA-MM-JJ`, jamais de `Date` : ce format se trie
 * dans l'ordre chronologique tel quel, et un `new Date('2026-09-04')` lu dans
 * le fuseau du navigateur donne le 3 septembre à l'ouest de Greenwich. Le même
 * piège a déjà coûté une soirée sur les ordres de transport le 04/09/2026, et
 * `planning.ts` porte la même précaution.
 */
export function periode(arrets: readonly Arret[]): Periode | null {
  const jours = arrets
    .map((a) => a.jour)
    .filter((j): j is string => typeof j === 'string' && j !== '');
  if (jours.length === 0) return null;

  // Pas de `jours[0]!` ni de `as string` : sous `noUncheckedIndexedAccess`, le
  // premier élément est `string | undefined` pour TypeScript, qui ne sait pas
  // qu'on vient de vérifier la longueur. Une graine `undefined` dit la même
  // chose sans laisser une assertion traîner dans le code.
  let debut: string | undefined;
  let fin: string | undefined;
  for (const jour of jours) {
    if (debut === undefined || jour < debut) debut = jour;
    if (fin === undefined || jour > fin) fin = jour;
  }
  if (debut === undefined || fin === undefined) return null;
  return { debut, fin, plusieursJours: debut !== fin };
}

/**
 * Les arrêts dans l'ordre de la ROUTE.
 *
 * `rang_tournee` d'abord, `nulls last` — une étape qu'on vient d'affecter sans
 * rang se range à la fin plutôt que de disparaître, exactement comme le fait
 * la requête de l'API. À rang égal ou absent, le jour puis l'heure tranchent,
 * puis l'identifiant : **le tri doit être total**, sans quoi deux affichages
 * successifs de la même tournée ne donnent pas le même ordre.
 */
export function ordreDeRoute(arrets: readonly Arret[]): readonly Arret[] {
  return [...arrets].sort((a, b) => {
    const ra = a.rang_tournee ?? Number.POSITIVE_INFINITY;
    const rb = b.rang_tournee ?? Number.POSITIVE_INFINITY;
    if (ra !== rb) return ra - rb;
    if (a.jour !== b.jour) return a.jour < b.jour ? -1 : 1;
    const ha = a.heure_debut ?? '';
    const hb = b.heure_debut ?? '';
    if (ha !== hb) return ha < hb ? -1 : 1;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });
}

// ---------------------------------------------------------------------------
// Le cumul — ce qui est à bord, arrêt par arrêt
// ---------------------------------------------------------------------------

/** Ce que pèse un arrêt en mètres linéaires. `null` si on ne sait pas. */
export function mlDeLArret(arret: Arret): number | null {
  const unitaire = nombre(arret.ml_unitaire);
  if (unitaire === null || unitaire < 0) return null;
  const quantite = nombre(arret.quantite);
  if (quantite === null || quantite < 0) return null;
  return unitaire * quantite;
}

/**
 * Un lot présent à bord, avec ce qu'il faut pour le dessiner et le nommer.
 *
 * **La composition, et pas seulement le total.** C'est ce qui permet à l'écran
 * de se placer au moment d'un arrêt et de montrer le camion tel qu'il est
 * là — et non seulement au point le plus chargé.
 */
export interface AuBord {
  readonly ot_id: string;
  /** L'arrêt de CHARGEMENT dont vient la marchandise, ou la livraison reprise d'un relais. */
  readonly arret_id: string;
  readonly numero: string;
  readonly support: string | null;
  readonly nombre: number;
  readonly ml_unitaire: number | null;
}

/** Ce qu'on sait à un arrêt donné. */
export interface Passage {
  readonly arret: Arret;
  /** Le rang dans la route, à partir de 1 — celui qu'on affiche. */
  readonly rang: number;
  /** Ce qui est à bord APRÈS cet arrêt, en ML. `null` si une pesée manque. */
  readonly aBord: number | null;
  /** De quoi est fait ce qui est à bord APRÈS cet arrêt. */
  readonly bord: readonly AuBord[];
  /**
   * Vrai quand cet arrêt est une livraison placée AVANT le chargement du même
   * ordre, alors que les deux sont dans cette tournée. C'est une vraie faute
   * de route, et la seule que l'écran signale.
   */
  readonly livreeAvantChargee: boolean;
}

/** Le remplissage d'une tournée entière. */
export interface Remplissage {
  readonly passages: readonly Passage[];
  /**
   * Ce que la tournée a eu de plus à bord, en ML. **C'est ce chiffre qui va
   * dans la jauge**, et non le total chargé dans la journée.
   */
  readonly maximum: number | null;
  /**
   * Ce avec quoi la tournée PART : le ML des ordres dont elle ne fait que la
   * livraison. Zéro dans le cas courant.
   */
  readonly auDepart: number;
  /** Ce qui reste à bord à la fin — non nul quand la tournée passe le relais. */
  readonly aLArrivee: number | null;
  /** Le nombre d'arrêts dont on ne sait pas le ML. */
  readonly inconnus: number;
  /** Les rangs des livraisons placées avant leur chargement. */
  readonly fautes: readonly number[];
  /**
   * Le rang, à partir de 1, de l'arrêt après lequel le maximum est atteint.
   * `null` sur une tournée vide, ou quand la tournée part chargée et ne
   * reprend rien : le maximum est alors au DÉPART, avant tout arrêt.
   */
  readonly rangDuMaximum: number | null;
  /** De quoi le camion est fait au point le plus chargé. C'est ce qu'on dessine. */
  readonly bordAuMaximum: readonly AuBord[];
}

/**
 * Ce qui est à bord à chaque arrêt, et le maximum atteint.
 *
 * **LE RELAIS FAUSSE LE CUMUL DANS LES DEUX SENS**, et c'est tout l'enjeu de
 * cette fonction :
 *
 *   - une tournée qui ne fait QUE LA LIVRAISON **part chargée**. Sans
 *     correction, le cumul démarrerait à zéro et plongerait dans le négatif à
 *     la première livraison — un camion qui livre du vide. On lui remet donc
 *     au départ le ML de tout ordre dont la livraison est ici sans son
 *     chargement ;
 *
 *   - une tournée qui ne fait QUE LE CHARGEMENT **finit pleine**. Celle-là ne
 *     demande aucune correction : ce qui n'a jamais été retiré reste à bord
 *     tout seul. C'est `aLArrivee` qui le montre — elle remet sa marchandise
 *     au relais, elle ne la fait pas disparaître.
 *
 * La correction se raisonne **arrêt par arrêt et non ordre par ordre** : un
 * ordre peut avoir un chargement et deux livraisons, dont une seule ici.
 *
 * **UN SEUL ML MANQUANT REND LE CUMUL INCONNU** à partir de là, et donc le
 * maximum inconnu. On ne compte pas un support sans dimensions pour zéro :
 * la jauge afficherait un taux rassurant et faux. L'écran doit dire qu'il ne
 * sait pas, et `inconnus` lui donne de quoi l'écrire.
 */
export function cumuler(arrets: readonly Arret[]): Remplissage {
  const route = ordreDeRoute(arrets);

  // Les ordres dont le CHARGEMENT est dans cette tournée. Sert deux fois : à
  // savoir avec quoi la tournée part, et à repérer les livraisons mal placées.
  const rangDuChargement = new Map<string, number>();
  route.forEach((arret, index) => {
    if (arret.type === 'chargement' && !rangDuChargement.has(arret.ot_id)) {
      rangDuChargement.set(arret.ot_id, index);
    }
  });

  // La composition de ce qui est à bord, tenue en parallèle du total. Une
  // entrée par ORDRE : c'est la maille du lot, celle que l'utilisateur déplace.
  const bord = new Map<string, AuBord>();

  let auDepart = 0;
  for (const arret of route) {
    if (arret.type !== 'livraison') continue;
    if (rangDuChargement.has(arret.ot_id)) continue;
    // Livrée ici, chargée ailleurs : elle est à bord au départ. Le lot porte
    // alors l'identifiant de SA LIVRAISON, faute de chargement à montrer.
    const ml = mlDeLArret(arret);
    if (ml !== null) auDepart += ml;
    const quantite = nombre(arret.quantite);
    bord.set(arret.ot_id, {
      ot_id: arret.ot_id,
      arret_id: arret.id,
      numero: arret.numero,
      support: arret.support,
      nombre: quantite !== null && quantite > 0 ? quantite : 0,
      ml_unitaire: nombre(arret.ml_unitaire),
    });
  }

  let aBord: number | null = auDepart;
  let maximum: number | null = auDepart;
  let rangDuMaximum: number | null = null;
  let bordAuMaximum: readonly AuBord[] = [...bord.values()];
  let inconnus = 0;
  const passages: Passage[] = [];
  const fautes: number[] = [];

  route.forEach((arret, index) => {
    const ml = mlDeLArret(arret);
    if (ml === null) {
      // On ne compte pas pour zéro : à partir d'ici, on ne sait plus.
      inconnus += 1;
      aBord = null;
      maximum = null;
    } else if (aBord !== null) {
      aBord = arret.type === 'chargement' ? aBord + ml : aBord - ml;
    }

    if (arret.type === 'chargement') {
      const quantite = nombre(arret.quantite);
      bord.set(arret.ot_id, {
        ot_id: arret.ot_id,
        arret_id: arret.id,
        numero: arret.numero,
        support: arret.support,
        nombre: quantite !== null && quantite > 0 ? quantite : 0,
        ml_unitaire: nombre(arret.ml_unitaire),
      });
    } else {
      bord.delete(arret.ot_id);
    }

    // Le maximum se relève APRÈS la mise à jour de la composition : c'est bien
    // le camion tel qu'il repart de cet arrêt qu'on veut dessiner.
    if (maximum !== null && aBord !== null && aBord > maximum) {
      maximum = aBord;
      rangDuMaximum = index + 1;
      bordAuMaximum = [...bord.values()];
    }

    const chargeApres = rangDuChargement.get(arret.ot_id);
    const faute =
      arret.type === 'livraison' &&
      chargeApres !== undefined &&
      chargeApres > index;
    if (faute) fautes.push(index + 1);

    passages.push({
      arret,
      rang: index + 1,
      aBord,
      bord: [...bord.values()],
      livreeAvantChargee: faute,
    });
  });

  return {
    passages,
    maximum,
    auDepart,
    aLArrivee: aBord,
    inconnus,
    fautes,
    rangDuMaximum,
    bordAuMaximum: maximum === null ? [] : bordAuMaximum,
  };
}

/**
 * Les lots tels qu'ils sont à un instant donné, DANS L'ORDRE DE RANGEMENT.
 *
 * `lots` donne l'ordre — au fond ce qui se livre en dernier, et le rang saisi
 * à la main quand il l'est. Cette fonction n'en retient que ce qui est encore
 * à bord, sans le réordonner : se placer au deuxième arrêt ne doit pas
 * réarranger le camion sous les yeux de l'utilisateur.
 */
export function lotsDuBord(
  lesLots: readonly Lot[],
  bord: readonly AuBord[],
): readonly Lot[] {
  const parArret = new Map(bord.map((b) => [b.arret_id, b.nombre]));
  return lesLots
    .filter((lot) => parArret.has(lot.arret_id))
    .map((lot) => ({ ...lot, nombre: parArret.get(lot.arret_id) ?? lot.nombre }))
    .filter((lot) => lot.nombre > 0);
}

// ---------------------------------------------------------------------------
// La jauge
// ---------------------------------------------------------------------------

/** Ce que la jauge affiche. */
export interface Jauge {
  /** Le maximum atteint, en ML. `null` quand une pesée manque. */
  readonly occupe: number | null;
  /** La capacité de la remorque attelée, en ML. `null` tant qu'aucune n'est attelée. */
  readonly capacite: number | null;
  /** De 0 à 1, et **au-delà de 1 en cas de dépassement**. `null` si on ne sait pas. */
  readonly part: number | null;
  /** Ce qui dépasse, en ML. Zéro quand ça rentre, `null` si on ne sait pas. */
  readonly enTrop: number | null;
  /** Ce que l'écran écrit : « 9,60 / 13,20 ML », « 9,60 ML · ? ». */
  readonly texte: string;
}

/**
 * La jauge d'une tournée.
 *
 * **TANT QU'AUCUNE REMORQUE N'EST ATTELÉE, LA CAPACITÉ EST INCONNUE.** L'écran
 * écrit « 9,60 ML · ? » et ne dessine pas de remorque. Inventer une capacité
 * par défaut laisserait croire que ça rentre.
 *
 * **La part n'est pas plafonnée à 1.** Le dépassement se MONTRE — jauge rouge,
 * palettes de trop posées derrière les portes — il ne se bloque pas.
 * L'exploitation sait des choses que le logiciel ignore, et un logiciel qui
 * bloque se contourne en saisissant faux.
 */
export function jauger(
  remplissage: Remplissage,
  capaciteMl: string | number | null,
): Jauge {
  const occupe = remplissage.maximum;
  const brute = nombre(capaciteMl);
  const capacite = brute !== null && brute > 0 ? brute : null;

  if (occupe === null) {
    return {
      occupe: null,
      capacite,
      part: null,
      enTrop: null,
      texte: capacite === null ? '? ML' : `? / ${mlLisible(capacite)} ML`,
    };
  }
  if (capacite === null) {
    return {
      occupe,
      capacite: null,
      part: null,
      enTrop: null,
      texte: `${mlLisible(occupe)} ML · ?`,
    };
  }
  // LE DEMI-CENTIMÈTRE, comme dans `mlLisible`. CONSTATÉ À L'ÉCRAN le
  // 05/09/2026 : une tournée qui ne fait que la livraison de 33 palettes
  // partait avec 13.200000000000001 ML pour une capacité de 13,2, et l'écran
  // annonçait « 0,00 ML de trop » sur un camion qui rentre pile. Un écart
  // qu'on ne sait pas écrire n'est pas un écart.
  const ecart = occupe - capacite;
  return {
    occupe,
    capacite,
    part: occupe / capacite,
    enTrop: ecart > 0.005 ? ecart : 0,
    texte: `${mlLisible(occupe)} / ${mlLisible(capacite)} ML`,
  };
}

// ---------------------------------------------------------------------------
// Le chiffre d'affaires
// ---------------------------------------------------------------------------

/** Une ligne de l'onglet « Ordres ». */
export interface LigneOrdre {
  readonly ot_id: string;
  readonly numero: string;
  readonly client: string | null;
  /** Le prix de vente de l'ordre. `null` s'il n'est pas renseigné. */
  readonly prix: number | null;
  /** Vrai quand cette tournée fait le chargement — c'est alors elle qui compte le prix. */
  readonly compte: boolean;
  /**
   * Le numéro de la tournée qui compte le prix, quand ce n'est pas celle-ci.
   * `null` quand le chargement n'est dans aucune tournée connue.
   */
  readonly compteSur: string | null;
}

/** Le récapitulatif de l'onglet « Ordres ». */
export interface Recapitulatif {
  readonly lignes: readonly LigneOrdre[];
  /** La somme des prix des ordres CHARGÉS par cette tournée. */
  readonly total: number;
  /** Le nombre d'ordres vus mais non comptés — ceux repris d'un relais. */
  readonly repris: number;
}

/**
 * Les ordres d'une tournée, et son chiffre d'affaires.
 *
 * **LE PRIX D'UN ORDRE EST COMPTÉ SUR LA TOURNÉE QUI FAIT SON CHARGEMENT.**
 * Sans cette règle, un ordre en relais serait compté deux fois et la somme des
 * tournées dépasserait le chiffre d'affaires réel. La tournée qui ne fait que
 * la livraison voit l'ordre, avec la mention « sur T-xxxx », et ne l'additionne
 * pas.
 *
 * **Corollaire à ne pas oublier** : le CA d'un ordre en relais va sur une seule
 * tournée, mais ses kilomètres sont partagés entre deux. Une marge calculée par
 * tournée serait donc fausse dès qu'il y a relais. La marge se calcule par
 * ordre ; le taux de remplissage par tournée. Chaque indicateur à sa maille.
 *
 * `chargementsAilleurs` rapproche un ordre du numéro de la tournée qui le
 * charge. L'écran le construit des autres tournées de la fenêtre ; il peut
 * être vide, et la mention est alors omise plutôt qu'inventée.
 */
export function recapituler(
  arrets: readonly Arret[],
  chargementsAilleurs: ReadonlyMap<string, string> = new Map(),
): Recapitulatif {
  const route = ordreDeRoute(arrets);
  const chargeIci = new Set(
    route.filter((a) => a.type === 'chargement').map((a) => a.ot_id),
  );

  const lignes: LigneOrdre[] = [];
  const vus = new Set<string>();
  let total = 0;
  let repris = 0;

  for (const arret of route) {
    if (vus.has(arret.ot_id)) continue;
    vus.add(arret.ot_id);

    const prix = nombre(arret.prix_vente);
    const compte = chargeIci.has(arret.ot_id);
    if (compte && prix !== null) total += prix;
    if (!compte) repris += 1;

    lignes.push({
      ot_id: arret.ot_id,
      numero: arret.numero,
      client: arret.client,
      prix,
      compte,
      compteSur: compte ? null : (chargementsAilleurs.get(arret.ot_id) ?? null),
    });
  }

  return { lignes, total, repris };
}

// ---------------------------------------------------------------------------
// Le rangement
// ---------------------------------------------------------------------------

/**
 * Un lot à charger : les unités d'un même arrêt de chargement.
 *
 * Le lot est l'unité que l'utilisateur déplace avec les flèches ↑ ↓ — jamais
 * la palette isolée. Le glisser-déposer a été écarté dès la 2e version de
 * l'essai.
 */
export interface Lot {
  /** L'identifiant de l'arrêt de chargement dont vient le lot. */
  readonly arret_id: string;
  readonly ot_id: string;
  readonly numero: string;
  readonly support: string | null;
  readonly nombre: number;
  /** Les ML d'une unité, pour le dessin. `null` si on ne sait pas. */
  readonly ml_unitaire: number | null;
  /**
   * Le rang de sortie : le rang dans la route de la livraison de cet ordre.
   * `Infinity` quand la marchandise reste à bord — elle va au fond.
   */
  readonly sortie: number;
  /** Le rang de chargement saisi à la main, quand il l'a été. */
  readonly rang_chargement: number | null;
}

/**
 * Les lots d'une tournée, dans l'ordre où on les pose.
 *
 * **AU FOND CE QUI SE LIVRE EN DERNIER.** C'est ce que fait le quai : ce qu'on
 * sort en premier doit être près des portes. Un lot dont la livraison n'est pas
 * dans cette tournée — un relais — sort après tout le monde, donc va au fond.
 *
 * **LE RANG SAISI À LA MAIN PRIME.** Quand `rang_chargement` est renseigné, il
 * l'emporte sur la déduction : le logiciel ignore ce qui est fragile, ce qui
 * sort au hayon, ce qui est ADR. C'est pour cela que l'utilisateur garde la
 * main.
 */
export function lots(arrets: readonly Arret[]): readonly Lot[] {
  const route = ordreDeRoute(arrets);

  const sortieParOrdre = new Map<string, number>();
  route.forEach((arret, index) => {
    if (arret.type === 'livraison' && !sortieParOrdre.has(arret.ot_id)) {
      sortieParOrdre.set(arret.ot_id, index);
    }
  });

  const chargeIci = new Set(
    route.filter((a) => a.type === 'chargement').map((a) => a.ot_id),
  );

  // Un lot par CHARGEMENT — et un lot par livraison REPRISE D'UN RELAIS, car
  // cette marchandise est bien à bord au départ et doit se dessiner. L'oublier
  // ferait partir le camion à moitié vide à l'écran alors que le cumul, lui,
  // la compte : deux panneaux qui se contredisent.
  const bruts = route
    .filter(
      (arret) =>
        arret.type === 'chargement' ||
        (arret.type === 'livraison' && !chargeIci.has(arret.ot_id)),
    )
    .map((arret) => {
      const quantite = nombre(arret.quantite);
      return {
        arret_id: arret.id,
        ot_id: arret.ot_id,
        numero: arret.numero,
        support: arret.support,
        nombre: quantite !== null && quantite > 0 ? Math.round(quantite) : 0,
        ml_unitaire: nombre(arret.ml_unitaire),
        sortie: sortieParOrdre.get(arret.ot_id) ?? Number.POSITIVE_INFINITY,
        rang_chargement: arret.rang_chargement,
      };
    })
    .filter((lot) => lot.nombre > 0);

  // Les rangs saisis d'abord, dans leur ordre ; puis la déduction, la sortie la
  // plus tardive au fond. `sort` est stable depuis ES2019 : à sortie égale,
  // l'ordre de la route tranche, et deux affichages donnent le même dessin.
  return bruts.sort((a, b) => {
    const sa = a.rang_chargement;
    const sb = b.rang_chargement;
    if (sa !== null && sb !== null) return sa - sb;
    if (sa !== null) return -1;
    if (sb !== null) return 1;
    return b.sortie - a.sortie;
  });
}

/** Une unité posée sur le plancher, en mètres, repère au fond à gauche. */
export interface Unite {
  readonly lot: number;
  readonly arret_id: string;
  /** Distance du fond, en mètres. */
  readonly x: number;
  /** Distance du bord gauche, en mètres. */
  readonly y: number;
  /** L'étage, à partir de 0. */
  readonly etage: number;
  /** L'encombrement au sol, en mètres. */
  readonly profondeur: number;
  readonly travers: number;
  /**
   * La hauteur d'une unité, en mètres. Ne sert QU'AU DESSIN : le mètre linéaire
   * ne la regarde pas, le gerbage passe par les étages, qui sont saisis.
   */
  readonly hauteur: number;
  /** Vrai quand l'unité ne rentre pas : elle se dessine derrière les portes. */
  readonly enTrop: boolean;
}

/** Ce que le rangement produit. */
export interface Rangement {
  readonly unites: readonly Unite[];
  /** La longueur réellement occupée par les rangs, en mètres. */
  readonly occupeM: number;
  /** Le nombre d'unités qui ne rentrent pas. */
  readonly enTrop: number;
  /** Le nombre de lots dont on ne sait pas ranger le support. */
  readonly ignores: number;
}

/** Comment une unité d'un lot se pose. Vient de `chargement.ts`. */
export interface PoseDuLot {
  /** L'encombrement en travers, en mètres. */
  readonly traversM: number;
  /** L'encombrement en profondeur, en mètres. */
  readonly profondeurM: number;
  /** Le nombre d'étages, saisi sur le support. */
  readonly etages: number;
  /**
   * La hauteur d'une unité, en mètres. Facultative, et pour le DESSIN seul :
   * un support sans hauteur se dessine à 1,00 m plutôt que plat, faute de quoi
   * il serait invisible.
   */
  readonly hauteurM: number | null;
}

/** La hauteur qu'on dessine quand le support n'en porte pas. */
export const HAUTEUR_PAR_DEFAUT_M = 1;

/** La largeur sur laquelle on range. La même que celle du mètre linéaire. */
export const LARGEUR_M = 2.4;

/**
 * Ranger les lots sur le plancher, rang par rang.
 *
 * **ON MÉLANGE LES SUPPORTS DANS UN MÊME RANG** quand la largeur restante le
 * permet : deux palettes de 0,80 et une demi-palette de 0,60 tiennent dans
 * 2,40 m. La première version posait chaque lot dans ses propres rangs, ce qui
 * laissait des trous — refusé par l'utilisateur le 04/09.
 *
 * **LA PROFONDEUR D'UN RANG EST CELLE DE SON UNITÉ LA PLUS PROFONDE.** C'est
 * physique : un rang ne peut pas être plus court que ce qu'il contient.
 *
 * **CE DESSIN NE DONNE PAS LE MÊME NOMBRE DE MÈTRES QUE LA JAUGE, et c'est
 * normal.** La jauge compte par CONVENTION : 0,40 ML la palette 80×120,
 * toujours, c'est-à-dire un tiers d'un rang plein. Le rangement, lui, place ce
 * qu'il y a vraiment. L'écart va **dans les deux sens** :
 *
 *   - **le dessin prend PLUS quand un rang est incomplet.** Deux palettes
 *     80×120 valent 0,80 ML par convention, mais occupent bien 1,20 m de
 *     plancher : le troisième emplacement du rang reste vide, et il est perdu ;
 *   - **le dessin prend MOINS quand un support s'intercale.** Une demi-palette
 *     glissée dans les 80 cm restants d'un rang de palettes ne coûte aucune
 *     profondeur de plus, alors que la convention lui compte 0,20 ML.
 *
 * Vérifié par les tests `ranger` de `tournee.test.ts`. **La jauge reste
 * l'indicateur qui fait foi** — c'est la convention du métier ; le dessin
 * montre comment ça se pose.
 *
 * **CE QUE LE RANGEMENT NE FERA PAS** : le rangement optimal. Le logiciel
 * ignore ce qui est fragile, ce qui sort au hayon, ce qui est ADR. C'est pour
 * cela que les flèches ↑ ↓ existent.
 */
export function ranger(
  lesLots: readonly Lot[],
  poseDuLot: (lot: Lot) => PoseDuLot | null,
  capaciteM: number | null,
): Rangement {
  const unites: Unite[] = [];
  let ignores = 0;

  // Le rang en cours : sa position au fond, sa profondeur, et la largeur déjà
  // prise. Un rang se ferme quand l'unité suivante n'y tient plus en largeur.
  let rangX = 0;
  let rangProfondeur = 0;
  let rangPris = 0;

  const fermerLeRang = (): void => {
    if (rangProfondeur > 0) {
      rangX += rangProfondeur;
      rangProfondeur = 0;
      rangPris = 0;
    }
  };

  lesLots.forEach((lot, indexDuLot) => {
    const pose = poseDuLot(lot);
    if (pose === null || pose.traversM <= 0 || pose.profondeurM <= 0) {
      ignores += 1;
      return;
    }
    const etages = Math.max(1, Math.floor(pose.etages));

    // GARDE CONTRE UNE BOUCLE SANS FIN. Un support plus large que 2,40 m ne
    // tient dans aucun rang, pas même dans un rang vide : sans ce contrôle, le
    // `while` ci-dessous fermerait indéfiniment un rang déjà fermé sans jamais
    // poser une unité. La base accepte de telles dimensions — elle ne sait pas
    // ce qu'on chargera demain —, donc le calcul doit s'en défendre.
    if (Math.floor((LARGEUR_M + 0.001) / pose.traversM) < 1) {
      ignores += 1;
      return;
    }

    let posees = 0;
    while (posees < lot.nombre) {
      // Combien tiennent encore en largeur dans le rang courant. La marge de
      // 1 mm absorbe la représentation en virgule flottante : 3 × 0,8 vaut
      // 2.4000000000000004, ce qui ferait refuser la troisième palette.
      const restant = LARGEUR_M - rangPris + 0.001;
      const deFront = Math.floor(restant / pose.traversM);
      if (deFront < 1) {
        fermerLeRang();
        continue;
      }

      const aPoser = Math.min(deFront * etages, lot.nombre - posees);
      const hauteur =
        pose.hauteurM !== null && pose.hauteurM > 0
          ? pose.hauteurM
          : HAUTEUR_PAR_DEFAUT_M;
      for (let k = 0; k < aPoser; k += 1) {
        const colonne = k % deFront;
        const etage = Math.floor(k / deFront);
        unites.push({
          lot: indexDuLot,
          arret_id: lot.arret_id,
          x: rangX,
          y: rangPris + colonne * pose.traversM,
          etage,
          profondeur: pose.profondeurM,
          travers: pose.traversM,
          hauteur,
          enTrop: false,
        });
      }
      posees += aPoser;

      const colonnesRemplies = Math.min(deFront, aPoser);
      rangPris += colonnesRemplies * pose.traversM;
      if (pose.profondeurM > rangProfondeur) rangProfondeur = pose.profondeurM;

      // Le rang est plein en largeur, ou le lot est fini : on ne rouvre pas un
      // rang à moitié pour le lot suivant tant qu'il y reste de la place.
      if (colonnesRemplies === deFront && posees < lot.nombre) {
        fermerLeRang();
      }
    }
  });

  fermerLeRang();
  const occupeM = rangX;

  // Ce qui dépasse la capacité se marque, il ne disparaît pas. Une remorque
  // inconnue ne fait rien dépasser : on ne sait pas ce qui rentre.
  const limite = capaciteM !== null && capaciteM > 0 ? capaciteM : null;
  const marquees = unites.map((u) =>
    limite !== null && u.x + u.profondeur > limite + 0.001
      ? { ...u, enTrop: true }
      : u,
  );

  return {
    unites: marquees,
    occupeM,
    enTrop: marquees.filter((u) => u.enTrop).length,
    ignores,
  };
}

/**
 * Déplacer un lot d'un cran, vers les portes ou vers le fond.
 *
 * Rend la liste des `arret_id` dans le nouvel ordre — c'est ce que l'écran
 * envoie comme rangs de chargement. Rend la MÊME liste, inchangée, quand le
 * mouvement n'a pas de sens : un lot déjà au bout ne bouge pas, et le bouton
 * doit alors être éteint plutôt que sans effet.
 */
export function deplacerLeLot(
  lesLots: readonly Lot[],
  arretId: string,
  sens: 'vers_le_fond' | 'vers_les_portes',
): readonly string[] {
  const ordre = lesLots.map((lot) => lot.arret_id);
  const depuis = ordre.indexOf(arretId);
  if (depuis === -1) return ordre;

  // Le premier de la liste est au FOND : c'est le premier posé. Aller vers le
  // fond, c'est donc reculer dans la liste.
  const vers = sens === 'vers_le_fond' ? depuis - 1 : depuis + 1;
  if (vers < 0 || vers >= ordre.length) return ordre;

  const deplace = [...ordre];
  const [pris] = deplace.splice(depuis, 1);
  if (pris === undefined) return ordre;
  deplace.splice(vers, 0, pris);
  return deplace;
}
